import React, { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Description, MainHeadline, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { DatePicker, SelectBox, ToggleRoundButton } from 'components/Form'
import { BlueButton } from 'components/Button'
import { FormLabel } from 'components/Label'
import { FlexRow } from 'components/Flex'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { addMonths, endOfMonth, format, isSameDay, startOfMonth } from 'date-fns'
import { useSelect } from 'hooks/form'
import { ConfirmModal } from 'components/Modal'
import { closeConfirmModal, openConfirmModal } from 'reducks/modal/operations'
import { endStoreTimesale, saveStoreTimesale } from 'reducks/storeTimesale/operations'
import { getStoreRef } from 'reducks/auth/selectors'
import { State } from 'index'
import { getStoreTimesale } from 'reducks/storeTimesale/selectors'
import { getHolidays, getLastOrderTime, getOrderStartTime } from 'reducks/store/selectors'
import { useEarliestBusinessTime } from 'hooks/businessHour'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { useGetTemporaryOpen } from 'hooks/storeCalendar'
import { dateToString, parseTimeToDate } from 'lib/helper'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { BusinessTimeType } from 'types/store'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { getBreads } from 'reducks/bread/selectors'
import { fetchBreads } from 'reducks/bread/operations'
import { useTimesWithRange } from 'hooks/store'
import startOfToday from 'date-fns/startOfToday'
import { StoreTimesaleBread } from 'types/storeTimesale'
const daysOfWeek = new DaysOfWeek()

const TimeSaleTemplate: FC = () => {
  const dispatch = useDispatch()
  const breads = useSelector((state: State) => getBreads(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const orderStartTime: BusinessTimeType = useSelector((state: State) => getOrderStartTime(state))
  const lastOrderTime: BusinessTimeType = useSelector((state: State) => getLastOrderTime(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const storeTimesale = useSelector((state: State) => getStoreTimesale(state))

  const now = useMemo(() => new Date(), [])
  const temporaryOpen = useGetTemporaryOpen(dateToString(now), storeCalendars)
  const dayOfWeek: DayOfWeekType = daysOfWeek.getDayOfWeek(now, specialHolidays)
  const earliestOrderStartTime = useEarliestBusinessTime([dayOfWeek], orderStartTime, 'order_start_time', temporaryOpen)
  const earliestLastOrderTime = useEarliestBusinessTime([dayOfWeek], lastOrderTime, 'last_order_time', temporaryOpen)

  const [isSaleStarted, setIsSaleStarted] = useState<boolean>(false)
  const { state: discountRate, handleState: handleDiscountRate } = useSelect('20')
  const [saleEndDate, setSaleEndDate] = useState<string>(format(new Date(), `yyyy-MM-dd'T'HH:mm`))

  const handleSaleEndDate = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      setSaleEndDate((prevState) => {
        if (!e.target.value) return prevState
        return e.target.value + 'T' + prevState.split('T')[1]
      }),
    [setSaleEndDate]
  )

  const handleSaleEndTime = useCallback(
    (value: string) =>
      setSaleEndDate((prevState) => {
        if (!value) return prevState
        return prevState.split('T')[0] + 'T' + value
      }),
    [setSaleEndDate]
  )

  const saleEndDateForModalDisplay = useMemo(() => format(new Date(saleEndDate), 'yyyy年MM月dd日 HH:mm'), [saleEndDate])

  const discountRates = useMemo(
    () =>
      Array.from({ length: 99 }, (_, i) => ({
        label: `${i + 1}%`,
        value: (i + 1).toString(),
      })),
    []
  )

  const selectableTimeOptions = useTimesWithRange(earliestOrderStartTime, earliestLastOrderTime)

  const handleIsSaleStarted = useCallback(() => {
    // TODO タイムセールが当日以外も設定できるようになったら過去日付でないことを確認する
    if (!isSameDay(startOfToday(), new Date(saleEndDate))) {
      alert('セール終了日時は当日のみ選択できます。')
      return
    }
    if (!isSaleStarted) {
      dispatch(openConfirmModal())
    } else {
      dispatch(closeConfirmModal())
      dispatch(endStoreTimesale())
      setIsSaleStarted(false)
    }
  }, [isSaleStarted, dispatch, setIsSaleStarted, saleEndDate])

  const onSaleStarted = useCallback(() => {
    setIsSaleStarted(true)
    dispatch(saveStoreTimesale({ storeRef, storeTimesale, saleEndDate, discountRate, breads }))
  }, [setIsSaleStarted, dispatch, storeRef, storeTimesale, saleEndDate, discountRate, breads])

  const goToTimeSaleProductPage = () => dispatch(push(`/time_sale/product`))

  // セール状態と割引率を設定
  useEffect(() => {
    if (!storeTimesale) return
    // パン毎に設定されている割引率の中の最大値を設定
    const maxDiscountRate = storeTimesale.breads.reduce((prev: number, current: StoreTimesaleBread) => {
      if (!current.discount_rate) return prev
      if (prev < current.discount_rate) return current.discount_rate
      return prev
    }, storeTimesale.discount_rate)

    setIsSaleStarted(storeTimesale.is_active)
    handleDiscountRate(maxDiscountRate)
  }, [storeTimesale])

  // 直近2ヶ月の休業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchClosedDates(startOfMonth(now), endOfMonth(addMonths(now, 1)), holidays, specialHolidays, storeRef))
  }, [storeRef, holidays, specialHolidays])

  // 臨時休業日・営業日を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  // 商品情報を取得
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchBreads(storeRef, 'public'))
  }, [storeRef])

  // セール状態に応じてセール終了日時を設定
  useEffect(() => {
    if (storeTimesale?.is_active) {
      // セール中は現在のセール終了日時を設定
      setSaleEndDate(format(storeTimesale.end_at.toDate(), `yyyy-MM-dd'T'HH:mm`))
    } else {
      // セール期間外は当日のラストオーダー時刻を設定
      setSaleEndDate(format(parseTimeToDate(earliestLastOrderTime), `yyyy-MM-dd'T'HH:mm`))
    }
  }, [storeTimesale, earliestLastOrderTime])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'タイムセール'} />
      <Spacer size={20} />
      <Description description={'セールを開始すると、セール終了日時まで対象商品に割引率が適応されます。'} />
      <Spacer size={20} />
      <FlexRow justifyContent={'between'}>
        <FormLabel
          id={'SaleStarted'}
          label={'タイムセールを開始する'}
          required={false}
          size={'medium'}
          width={'auto'}
        />
        <ToggleRoundButton permission={'time_sale'} toggle={handleIsSaleStarted} on={isSaleStarted} />
      </FlexRow>
      <Spacer size={20} />
      <FormLabel id={'saleEndDate'} label={'セール終了日時'} required={true} size={'medium'} />
      <Spacer size={4} />
      <SubDescription description={'本日のラストオーダー時刻まで設定できます'} />
      <Spacer size={8} />
      <FlexRow justifyContent={'center'}>
        <DatePicker
          // TODO: タイムセールが当日以外も設定できるようになったらdisabledを外す
          disabled
          onChange={handleSaleEndDate}
          type={'date'}
          value={saleEndDate.split('T')[0]}
        />
        <Spacer size={8} />
        <SelectBox options={selectableTimeOptions} select={handleSaleEndTime} value={saleEndDate.split('T')[1]} />
      </FlexRow>
      <Spacer size={20} />
      <FormLabel id={'discountRate'} label={'割引率'} required={true} size={'medium'} />
      <Spacer size={8} />
      <SelectBox options={discountRates} select={handleDiscountRate} value={discountRate} />
      <Spacer size={20} />
      <FormLabel id={'saleItems'} label={'セール対象商品'} required={true} size={'medium'} />
      <Spacer size={8} />
      <SubDescription
        description={
          'デフォルトのセール対象は、sacriで販売中かつ在庫がセール中期間に存在するすべての商品になります。セール対象外にしたい商品がある場合は「セール対象商品を選択する」より変更してください。'
        }
      />
      <Spacer size={8} />
      <BlueButton
        endIconPath={'/icon/white/arrow_next_small.svg'}
        label={'セール対象商品を選択する'}
        onClick={goToTimeSaleProductPage}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={28} />
      <ConfirmModal
        cautionActionLabel={'セールを開始する'}
        dataCy={'time_sale_started-modal'}
        description={`在庫があるセール対象商品は
          ${saleEndDateForModalDisplay}まで${discountRate}%オフになります。よろしいですか？`}
        title={'タイムセールを開始します'}
        actionButtonColor={'blue'}
        onClose={onSaleStarted}
      />
    </div>
  )
}
export default TimeSaleTemplate
